import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { DragEvent } from 'react';
import { ChevronRight, GripVertical, Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { NodeStruct } from '@/types/node-types';
import { getNodeIconColor } from '@/constants/node-styling';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TOOLTIP_DELAY_MS } from '@/constants/deprecated-nodes';
import { IconTile } from '../icon-tile';
import { NodeItem } from './node-list';

interface GroupedToolNodeListProps {
  nodes: NodeStruct[];
  onDragStart?: (event: DragEvent<HTMLDivElement>, node: NodeStruct) => void;
  onAddNode?: (node: NodeStruct) => void;
  onDragStartCategory?: (
    event: DragEvent<HTMLDivElement>,
    categoryName: string,
    nodes: NodeStruct[],
  ) => void;
  onAddCategory?: (categoryName: string, nodes: NodeStruct[]) => void;
  onClick?: (node: NodeStruct) => void;
  showGripIcon?: boolean;
  isLocked?: boolean;
  /**
   * Initial state for subcategories. Defaults to all closed.
   * - 'all-open': All subcategories start open
   * - 'all-closed': All subcategories start closed
   * - string[]: Specific subcategory names to start open
   */
  initialSubCategoryState?: 'all-open' | 'all-closed' | string[];
  /** Index of the currently highlighted node for keyboard navigation */
  highlightedIndex?: number;
  /** Offset to add to data-node-index for proper scroll-into-view in parent container */
  highlightedIndexOffset?: number;
  /** Optional ref to receive the current flat display order (for keyboard selection in parent) */
  flatDisplayOrderRef?: React.MutableRefObject<NodeStruct[] | null>;
  /** Optional callback when flat display order changes (for NodeList to build full list) */
  onFlatDisplayOrderChange?: (order: NodeStruct[]) => void;
  /** Check if a node is currently multi-selected */
  isNodeSelected?: (nodeId: string) => boolean;
  /** Returns the repeat count for a selected node (for duplicate badge) */
  getNodeSelectionCount?: (nodeId: string) => number;
  /** Toggle multi-selection for a node (triggered by Cmd/Ctrl+click) */
  onToggleSelect?: (node: NodeStruct) => void;
  /** Plain click: select a single node, clearing previous selection */
  onSelectSingle?: (node: NodeStruct) => void;
  /** Shift+click: range select from anchor to target */
  onShiftSelect?: (node: NodeStruct) => void;
  /** Double-click on a node row: immediately add the node, bypassing selection. */
  onDoubleClickAdd?: (node: NodeStruct) => void;
  /** Double-click on an MCP server row: immediately add all its sub-tools. */
  onDoubleClickAddCategory?: (nodes: NodeStruct[]) => void;
  /** Called when a drag operation ends (for clearing selection) */
  onDragEnd?: () => void;
  /** Called when an MCP server category row is clicked (selects all its nodes) */
  onSelectCategory?: (nodes: NodeStruct[]) => void;
  /** Additively add nodes to existing selection (used for Cmd/Ctrl+click on categories) */
  onAddToSelection?: (nodes: NodeStruct[]) => void;
  /** Range-select from anchor to target using a custom ordered list (used for Shift+click on categories) */
  onRangeSelect?: (targetNodeId: string, allOrderedNodeIds: string[]) => void;
}

const ServerCategoryRow: React.FC<{
  categoryName: string;
  nodes: NodeStruct[];
  isExpanded: boolean;
  onClick: (e: React.MouseEvent) => void;
  onChevronClick: () => void;
  onDragStartCategory?: (
    event: DragEvent<HTMLDivElement>,
    categoryName: string,
    nodes: NodeStruct[],
  ) => void;
  onAddCategory?: (categoryName: string, nodes: NodeStruct[]) => void;
  /** Double-click on the row adds every sub-tool immediately (bypasses selection). */
  onDoubleClickAddAll?: (nodes: NodeStruct[]) => void;
  isLocked?: boolean;
}> = ({
  categoryName,
  nodes,
  isExpanded,
  onClick,
  onChevronClick,
  onDragStartCategory,
  onAddCategory,
  onDoubleClickAddAll,
  isLocked = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAddBtnHovered, setIsAddBtnHovered] = useState(false);
  const isDraggable = Boolean(onDragStartCategory) && !isLocked;
  const representativeNode = nodes[0];
  const iconColor = getNodeIconColor(
    representativeNode?.node_type,
    representativeNode?.icon_color,
  );
  const useThemeColors = iconColor === null;
  const showAddBtnTooltip = Boolean(isAddBtnHovered && onAddCategory);

  return (
    <Tooltip delayDuration={isAddBtnHovered ? 0 : TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'group flex w-full items-center gap-3 rounded-xl p-2 text-left text-sm transition-all duration-200 cursor-pointer',
            !isExpanded &&
              'bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-900',
            isHovered && !isExpanded && 'bg-neutral-200',
            isDraggable && 'cursor-grab active:cursor-grabbing',
            isLocked && 'opacity-60 cursor-not-allowed',
          )}
          draggable={isDraggable}
          onClick={(e) => {
            // Swallow the 2nd click of a plain dblclick pair (e.detail === 2)
            // so we don't toggle the category selection off right before
            // onDoubleClick fires. Modifier keys are reserved for selection
            // (Cmd-add / Shift-range) — let those through unchanged.
            if (
              onDoubleClickAddAll &&
              e.detail >= 2 &&
              !e.shiftKey &&
              !e.metaKey &&
              !e.ctrlKey
            ) {
              e.stopPropagation();
              return;
            }
            e.stopPropagation();
            onClick(e);
          }}
          onDoubleClick={(e) => {
            if (!onDoubleClickAddAll || isLocked) return;
            // Modifier keys are for selection; don't trigger add.
            if (e.shiftKey || e.metaKey || e.ctrlKey) return;
            e.stopPropagation();
            onDoubleClickAddAll(nodes);
          }}
          onDragStart={(e) => onDragStartCategory?.(e, categoryName, nodes)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <IconTile
            icon={representativeNode?.icon}
            backgroundColor={iconColor || ''}
            iconColor={iconColor ? '#ffffff' : ''}
            size='small'
            className={useThemeColors ? 'bg-black dark:bg-white' : ''}
            iconClassName={
              useThemeColors
                ? 'w-5! h-5! text-white dark:text-black'
                : 'w-5! h-5!'
            }
          />
          <div className='flex-1 min-w-0 truncate'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium text-gray-900 truncate dark:text-white'>
                {categoryName}
              </span>
              <span className='text-xs text-muted-foreground shrink-0'>
                {nodes.length} {nodes.length === 1 ? 'tool' : 'tools'}
              </span>
            </div>
          </div>
          {onAddCategory && (
            <div
              onMouseEnter={() => setIsAddBtnHovered(true)}
              onMouseLeave={() => setIsAddBtnHovered(false)}
              onFocus={() => setIsAddBtnHovered(true)}
              onBlur={() => setIsAddBtnHovered(false)}
            >
              <Button
                variant='ghost'
                size='sm'
                disabled={isLocked}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isLocked) onAddCategory(categoryName, nodes);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
                className='opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded shrink-0 dark:text-white dark:hover:bg-neutral-800'
              >
                <Plus className='size-4 text-gray-600 dark:text-white' />
              </Button>
            </div>
          )}
          {isDraggable && (
            <GripVertical className='size-4 text-gray-600 dark:text-white shrink-0' />
          )}
          <button
            type='button'
            aria-label={`Toggle ${categoryName}`}
            aria-expanded={isExpanded}
            className={cn(
              'shrink-0 transform transition-transform duration-200 p-1 -m-1 rounded hover:bg-neutral-300 dark:hover:bg-neutral-700 cursor-pointer',
              isExpanded ? 'rotate-90' : 'rotate-0',
            )}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onChevronClick();
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <ChevronRight className='size-3.5' />
          </button>
        </div>
      </TooltipTrigger>
      {showAddBtnTooltip && (
        <TooltipContent
          side='right'
          align='center'
          sideOffset={12}
          className='max-w-xs'
        >
          <p>
            {isLocked
              ? 'Workflow is locked'
              : `Add all ${nodes.length} tools to canvas`}
          </p>
        </TooltipContent>
      )}
    </Tooltip>
  );
};

/**
 * Groups Tool nodes by their sub-category (tool_category field) and renders them with collapsible labels.
 * Used in both node-list and node-selector for consistent Tool node grouping.
 */
export const GroupedToolNodeList: React.FC<GroupedToolNodeListProps> = ({
  nodes,
  onDragStart,
  onAddNode,
  onDragStartCategory,
  onAddCategory,
  onClick,
  showGripIcon = true,
  isLocked = false,
  initialSubCategoryState = 'all-closed',
  highlightedIndex = -1,
  highlightedIndexOffset = 0,
  flatDisplayOrderRef,
  onFlatDisplayOrderChange,
  isNodeSelected,
  getNodeSelectionCount,
  onToggleSelect,
  onSelectSingle,
  onShiftSelect,
  onDoubleClickAdd,
  onDoubleClickAddCategory,
  onDragEnd,
  onSelectCategory,
  onAddToSelection,
  onRangeSelect,
}) => {
  // Group Tool nodes by their tool_category
  const groupedNodes = useMemo(() => {
    const groups: Record<string, NodeStruct[]> = {};
    const noSubCategory: NodeStruct[] = [];

    nodes.forEach((node) => {
      if (node.tool_category) {
        if (!groups[node.tool_category]) {
          groups[node.tool_category] = [];
        }
        groups[node.tool_category].push(node);
      } else {
        noSubCategory.push(node);
      }
    });

    return { groups, noSubCategory };
  }, [nodes]);

  const subCategories = useMemo(
    () => Object.keys(groupedNodes.groups).sort(),
    [groupedNodes.groups],
  );

  const hasSubCategories = subCategories.length > 0;

  // State for tracking which subcategories are expanded
  const [openSubCategories, setOpenSubCategories] = useState<string[]>([]);

  // Track which subcategories have already been initialized so we don't
  // overwrite the user's manual toggle state on subsequent renders. The
  // parent can re-render with a new `nodes` array reference (same content),
  // which would otherwise cause `subCategories` to be a new array reference
  // and re-trigger this effect, clobbering the user's open/closed choice.
  const initializedSubCategoriesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const newSubCategories = subCategories.filter(
      (sc) => !initializedSubCategoriesRef.current.has(sc),
    );
    if (newSubCategories.length === 0) return;

    newSubCategories.forEach((sc) =>
      initializedSubCategoriesRef.current.add(sc),
    );

    let toOpen: string[] = [];
    if (initialSubCategoryState === 'all-open') {
      toOpen = newSubCategories;
    } else if (Array.isArray(initialSubCategoryState)) {
      toOpen = newSubCategories.filter((sc) =>
        initialSubCategoryState.includes(sc),
      );
    }

    if (toOpen.length === 0) return;
    setOpenSubCategories((prev) => [
      ...prev,
      ...toOpen.filter((sc) => !prev.includes(sc)),
    ]);
  }, [subCategories, initialSubCategoryState]);

  // Compute flat display order for keyboard navigation highlighting
  // Only includes nodes from expanded subcategories to match visual display
  const flatDisplayOrder = useMemo(() => {
    const order: NodeStruct[] = [...groupedNodes.noSubCategory];
    subCategories.forEach((subCategory) => {
      // Only include nodes from expanded subcategories
      if (openSubCategories.includes(subCategory)) {
        order.push(...groupedNodes.groups[subCategory]);
      }
    });
    return order;
  }, [groupedNodes, subCategories, openSubCategories]);

  // Complete ordered list of ALL node IDs (including collapsed categories)
  // Used for Shift+click range selection on categories
  const allOrderedNodeIds = useMemo(() => {
    const ids: string[] = groupedNodes.noSubCategory.map((n) => n.node_id);
    subCategories.forEach((subCategory) => {
      ids.push(...groupedNodes.groups[subCategory].map((n) => n.node_id));
    });
    return ids;
  }, [groupedNodes, subCategories]);

  // Expose flat display order to parent for keyboard selection (same order as highlight)
  useEffect(() => {
    if (flatDisplayOrderRef) {
      flatDisplayOrderRef.current = flatDisplayOrder;
      return () => {
        flatDisplayOrderRef.current = null;
      };
    }
  }, [flatDisplayOrder, flatDisplayOrderRef]);

  useEffect(() => {
    onFlatDisplayOrderChange?.(flatDisplayOrder);
  }, [flatDisplayOrder, onFlatDisplayOrderChange]);

  // Get the highlighted node ID based on flat index
  const highlightedNodeId = useMemo(() => {
    if (highlightedIndex >= 0 && highlightedIndex < flatDisplayOrder.length) {
      return flatDisplayOrder[highlightedIndex]?.node_id;
    }
    return null;
  }, [highlightedIndex, flatDisplayOrder]);

  // Create a map of node_id to global index for data-node-index attribute (used for scroll-into-view)
  const nodeIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    flatDisplayOrder.forEach((node, idx) => {
      if (node.node_id) {
        // Offset so the index is global (relative to parent container)
        map.set(node.node_id, idx + highlightedIndexOffset);
      }
    });
    return map;
  }, [flatDisplayOrder, highlightedIndexOffset]);

  const handleSubCategoryToggle = useCallback((subCategory: string) => {
    setOpenSubCategories((prev) =>
      prev.includes(subCategory)
        ? prev.filter((sc) => sc !== subCategory)
        : [...prev, subCategory],
    );
  }, []);

  // Check if all nodes in a category are selected (used for container highlight and toggle)
  const isCategoryFullySelected = useCallback(
    (categoryNodes: NodeStruct[]) => {
      if (!isNodeSelected || categoryNodes.length === 0) return false;
      return categoryNodes.every((node) => isNodeSelected(node.node_id));
    },
    [isNodeSelected],
  );

  // Click on MCP server row: select based on modifier keys (does NOT expand/collapse)
  const handleCategoryClick = useCallback(
    (subCategory: string, e: React.MouseEvent) => {
      const categoryNodes = groupedNodes.groups[subCategory];
      if (!categoryNodes || categoryNodes.length === 0) return;

      if (e.shiftKey && onRangeSelect) {
        // Shift+click: range-select from anchor through end of this category
        const lastNodeInCategory = categoryNodes[categoryNodes.length - 1];
        onRangeSelect(lastNodeInCategory.node_id, allOrderedNodeIds);
      } else if ((e.metaKey || e.ctrlKey) && onAddToSelection) {
        // Cmd/Ctrl+click: add all category nodes to existing selection
        onAddToSelection(categoryNodes);
      } else if (isCategoryFullySelected(categoryNodes)) {
        // Plain click on already-selected category: deselect all
        onSelectCategory?.([]);
      } else {
        // Plain click: select all nodes in this category (replacing previous)
        onSelectCategory?.(categoryNodes);
      }
    },
    [
      groupedNodes.groups,
      onSelectCategory,
      onAddToSelection,
      onRangeSelect,
      allOrderedNodeIds,
      isCategoryFullySelected,
    ],
  );

  // Wrap onShiftSelect so individual node Shift+clicks also use the full
  // ordered list (including collapsed categories), not just visible nodes.
  const handleNodeShiftSelect = useCallback(
    (node: NodeStruct) => {
      if (onRangeSelect) {
        onRangeSelect(node.node_id, allOrderedNodeIds);
      } else {
        onShiftSelect?.(node);
      }
    },
    [onRangeSelect, allOrderedNodeIds, onShiftSelect],
  );

  // If no sub-categories, render flat list
  if (!hasSubCategories) {
    return (
      <div className='space-y-2 p-0.5'>
        {nodes.map((node, idx) => (
          <div
            key={node.node_id || node.display_name || idx}
            data-node-index={
              node.node_id ? nodeIndexMap.get(node.node_id) : idx
            }
            className={cn(
              highlightedNodeId === node.node_id &&
                'ring-2 ring-primary rounded-lg',
            )}
          >
            <NodeItem
              node={node}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onAddNode={onAddNode}
              onClick={onClick}
              showGripIcon={showGripIcon}
              isLocked={isLocked}
              selected={isNodeSelected?.(node.node_id)}
              selectionCount={getNodeSelectionCount?.(node.node_id)}
              onToggleSelect={onToggleSelect}
              onSelectSingle={onSelectSingle}
              onShiftSelect={handleNodeShiftSelect}
              onDoubleClickAdd={onDoubleClickAdd}
            />
          </div>
        ))}
      </div>
    );
  }

  // Render grouped by sub-category
  return (
    <div className='space-y-2 p-0.5'>
      {/* Nodes without sub-category first */}
      {groupedNodes.noSubCategory.length > 0 && (
        <div className='space-y-2'>
          {groupedNodes.noSubCategory.map((node, idx) => (
            <div
              key={node.node_id || node.display_name || idx}
              data-node-index={
                node.node_id ? nodeIndexMap.get(node.node_id) : idx
              }
              className={cn(
                highlightedNodeId === node.node_id &&
                  'ring-2 ring-primary rounded-lg',
              )}
            >
              <NodeItem
                node={node}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onAddNode={onAddNode}
                onClick={onClick}
                showGripIcon={showGripIcon}
                isLocked={isLocked}
                selected={isNodeSelected?.(node.node_id)}
                selectionCount={getNodeSelectionCount?.(node.node_id)}
                onToggleSelect={onToggleSelect}
                onSelectSingle={onSelectSingle}
                onShiftSelect={handleNodeShiftSelect}
                onDoubleClickAdd={onDoubleClickAdd}
              />
            </div>
          ))}
        </div>
      )}

      {/* Grouped nodes by sub-category (MCP server) */}
      {subCategories.map((subCategory) => {
        const isExpanded = openSubCategories.includes(subCategory);
        const categoryNodes = groupedNodes.groups[subCategory];
        const isCategorySelected = isCategoryFullySelected(categoryNodes);
        return (
          <div
            key={subCategory}
            className={cn(
              'rounded-xl transition-all duration-200',
              isExpanded && 'bg-neutral-100 dark:bg-neutral-800',
              isExpanded &&
                !isCategorySelected &&
                'ring-1 ring-neutral-200 dark:ring-neutral-700',
              isCategorySelected && 'ring-2 ring-primary/50',
            )}
          >
            <ServerCategoryRow
              categoryName={subCategory}
              nodes={categoryNodes}
              isExpanded={isExpanded}
              onClick={(e) => handleCategoryClick(subCategory, e)}
              onChevronClick={() => handleSubCategoryToggle(subCategory)}
              onDragStartCategory={onDragStartCategory}
              onAddCategory={onAddCategory}
              onDoubleClickAddAll={onDoubleClickAddCategory}
              isLocked={isLocked}
            />

            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className='overflow-hidden'
                >
                  {/* Tool nodes inside the container use a darker bg for contrast */}
                  <div className='space-y-1.5 px-2 py-2 [&_.group]:bg-neutral-200 [&_.group]:dark:bg-neutral-900'>
                    {categoryNodes.map((node, idx) => (
                      <div
                        key={node.node_id || node.display_name || idx}
                        data-node-index={
                          node.node_id
                            ? nodeIndexMap.get(node.node_id)
                            : undefined
                        }
                        className={cn(
                          highlightedNodeId === node.node_id &&
                            'ring-2 ring-primary rounded-lg',
                        )}
                      >
                        <NodeItem
                          node={node}
                          onDragStart={onDragStart}
                          onDragEnd={onDragEnd}
                          onAddNode={onAddNode}
                          onClick={onClick}
                          showGripIcon={showGripIcon}
                          isLocked={isLocked}
                          selected={isNodeSelected?.(node.node_id)}
                          selectionCount={getNodeSelectionCount?.(node.node_id)}
                          onToggleSelect={onToggleSelect}
                          onSelectSingle={onSelectSingle}
                          onShiftSelect={handleNodeShiftSelect}
                          onDoubleClickAdd={onDoubleClickAdd}
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
